import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

export interface PaletteItem {
  id: string;
  kind: "nav" | "action" | "ask";
  label: string;
  hint?: string;
  route?: any[];
  action?: string;
  kbd?: string;
}

@Component({
  selector: "app-exp-command-palette",
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="palette-back" [class.open]="open" (click)="onBackdrop($event)">
      <div class="palette" (click)="$event.stopPropagation()">
        <input
          #input
          class="palette-input"
          type="text"
          placeholder="Type a command, a portfolio step, or ask Alti…"
          [(ngModel)]="query"
          (keydown)="onKey($event)"
        />
        <div class="palette-list">
          @for (item of filtered; track item; let i = $index) {
            <button
              type="button"
              class="palette-item"
              [class.sel]="i === selectedIndex"
              (mouseenter)="selectedIndex = i"
              (click)="choose(item)"
            >
              <span>{{ item.label }}</span>
              @if (item.hint) {
                <span class="pi-kind">{{ item.hint }}</span>
              }
              @if (!item.hint) {
                <span class="pi-kind">{{ item.kind }}</span>
              }
              @if (item.kbd) {
                <span class="kbd">{{ item.kbd }}</span>
              }
            </button>
          }
          @if (!filtered.length) {
            <div
              class="palette-item"
              style="color: var(--text-dim); pointer-events: none;"
            >
              No match. Press Enter to ask Alti: "{{ query }}"
            </div>
          }
        </div>
      </div>
    </div>
  `,
})
export class CommandPaletteComponent implements AfterViewInit, OnChanges {
  @Input() open = false;
  @Input() items: PaletteItem[] = [];
  @Output() pick = new EventEmitter<PaletteItem>();
  @Output() askAlti = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild("input") input?: ElementRef<HTMLInputElement>;

  query = "";
  selectedIndex = 0;

  get filtered(): PaletteItem[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.items.slice(0, 30);
    return this.items
      .filter(
        (i) =>
          i.label.toLowerCase().includes(q) ||
          (i.hint || "").toLowerCase().includes(q),
      )
      .slice(0, 30);
  }

  ngAfterViewInit(): void {
    if (this.open) setTimeout(() => this.input?.nativeElement.focus(), 30);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["open"] && this.open) {
      this.query = "";
      this.selectedIndex = 0;
      setTimeout(() => this.input?.nativeElement.focus(), 30);
    }
  }

  onBackdrop(_e: MouseEvent): void {
    this.closed.emit();
  }

  onKey(e: KeyboardEvent): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.selectedIndex = Math.min(
        this.filtered.length - 1,
        this.selectedIndex + 1,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.selectedIndex = Math.max(0, this.selectedIndex - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = this.filtered[this.selectedIndex];
      if (item) this.choose(item);
      else if (this.query.trim()) {
        this.askAlti.emit(this.query.trim());
        this.closed.emit();
      }
    } else if (e.key === "Escape") {
      this.closed.emit();
    }
  }

  choose(item: PaletteItem): void {
    this.pick.emit(item);
  }

  @HostListener("document:keydown.escape")
  onEsc(): void {
    if (this.open) this.closed.emit();
  }
}
